import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Search, X, LayoutGrid, List, Home, ChevronRight, FolderOpen, AlertTriangle, Loader2, Folder } from 'lucide-react';
import githubApi from './services/githubApi';
import CommitHistory from './components/CommitHistory';
import Header from './components/Header';
import Footer from './components/Footer';
import { parseFileInfo, shouldSkipDirectory } from './utils/fileUtils';
import { saveToCache, loadFromCache, clearCache } from './utils/cache';
import FileItem from './components/FileItem';
import TreeItem from './components/TreeItem';

// Configuration from environment variables
const APP_CONFIG = {
  title: process.env.REACT_APP_TITLE || 'GitHub Explorer',
  description: process.env.REACT_APP_DESCRIPTION || 'Repository Browser',
  githubOwner: process.env.REACT_APP_GITHUB_OWNER || '',
  githubRepo: process.env.REACT_APP_GITHUB_REPO || '',
  githubUrl: `https://github.com/${process.env.REACT_APP_GITHUB_OWNER}/${process.env.REACT_APP_GITHUB_REPO}`,
  author: {
    name: process.env.REACT_APP_AUTHOR_NAME || 'Author',
    website: process.env.REACT_APP_AUTHOR_WEBSITE || '#',
  },
  profile: {
    link: process.env.REACT_APP_PROFILE_LINK || '',
    text: process.env.REACT_APP_PROFILE_TEXT || 'Profile',
  },
};

function App() {
  // State management
  const [repoStructure, setRepoStructure] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [currentView, setCurrentView] = useState('grid');
  const [loadedPaths, setLoadedPaths] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Theme state
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Search results state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Stats
  const [totalSolutions, setTotalSolutions] = useState(0);
  const [totalFolders, setTotalFolders] = useState(0);

  // Last updated
  const [lastUpdated, setLastUpdated] = useState(null);

  // Router
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize app
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    initializeApp();
  }, []);

  // Update theme
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Update stats when files change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setTotalSolutions(allFiles.length);
    setTotalFolders(countFolders(repoStructure));
  }, [allFiles, repoStructure]);

  // Apply filters when files or filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    applyFilters();
  }, [allFiles, typeFilter, languageFilter]);

  // Handle search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    handleSearch();
  }, [searchQuery, allFiles]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const handleClearCache = () => {
    clearCache();
    window.location.reload();
  };

  const initializeApp = async () => {
    setIsLoading(true);
    setError(null);

    // Try to load from cache first
    const cachedData = loadFromCache();
    if (cachedData) {
      console.log('Loaded from cache');
      setRepoStructure(cachedData.repoStructure);
      setAllFiles(cachedData.allFiles);
      setFilteredFiles([...cachedData.allFiles]);
      setLoadedPaths(cachedData.loadedPaths);
      setIsLoading(false);
      // Still fetch last updated
      fetchLastUpdated();
      return;
    }

    try {
      await loadRepositoryStructure();
      console.log('Repository structure loaded, files will load on-demand');
      // Fetch last updated
      fetchLastUpdated();
    } catch (error) {
      console.error('Failed to initialize app:', error);
      setError(error.message === 'RATE_LIMIT_EXCEEDED' ?
        'GitHub API rate limit exceeded. Please try again later.' :
        'Failed to load repository data. Please check your internet connection.');
    }

    setIsLoading(false);
  };

  const fetchLastUpdated = async () => {
    try {
      const commits = await githubApi.getLatestCommit();
      if (commits && commits.length > 0) {
        setLastUpdated(commits[0].commit.author.date);
      }
    } catch (error) {
      console.error('Failed to fetch last updated:', error);
    }
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 30) {
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const loadRepositoryStructure = async () => {
    try {
      const contents = await githubApi.getContents();

      const structure = {
        name: APP_CONFIG.githubRepo || 'Repository',
        type: 'folder',
        children: {}
      };

      const rootFiles = [];

      // Process top-level items
      for (const item of contents) {
        if (item.type === 'dir' && !shouldSkipDirectory(item.name)) {
          structure.children[item.name] = {
            name: item.name,
            type: 'folder',
            children: {},
            loaded: false
          };
        } else if (item.type === 'file') {
          // Add root-level files
          const fileInfo = parseFileInfo(item, '');
          if (fileInfo) {
            rootFiles.push(fileInfo);
          }
        }
      }

      setRepoStructure(structure);
      setAllFiles(rootFiles);
      setLoadedPaths(prev => new Set([...prev, '']));

      // Save to cache
      saveToCache({
        repoStructure: structure,
        allFiles: rootFiles,
        loadedPaths: new Set([''])
      });

    } catch (error) {
      console.error('Failed to load repository structure:', error);
      throw error;
    }
  };

  const loadDirectoryContents = async (path) => {
    if (loadedPaths.has(path)) {
      return;
    }

    try {
      const contents = await githubApi.getContents(path);

      // Get the node to update
      const pathParts = path ? path.split('/') : [];
      let targetNode = repoStructure;

      for (const part of pathParts) {
        if (targetNode.children && targetNode.children[part]) {
          targetNode = targetNode.children[part];
        } else {
          return; // Path doesn't exist
        }
      }

      // Clear existing children and add new ones
      targetNode.children = {};
      const newFiles = [];

      for (const item of contents) {
        if (item.type === 'dir' && !shouldSkipDirectory(item.name)) {
          targetNode.children[item.name] = {
            name: item.name,
            type: 'folder',
            children: {},
            loaded: false
          };
        } else if (item.type === 'file') {
          // Add file to allFiles
          const fileInfo = parseFileInfo(item, path);
          if (fileInfo) {
            newFiles.push(fileInfo);
          }
        }
      }

      targetNode.loaded = true;

      // Update state
      setAllFiles(prev => {
        const updated = [...prev, ...newFiles];
        // Remove duplicates
        const unique = updated.filter((file, index, self) =>
          index === self.findIndex(f => f.name === file.name && f.path === file.path)
        );
        return unique;
      });

      setLoadedPaths(prev => new Set([...prev, path]));
      setRepoStructure({ ...repoStructure });

      // Save to cache
      saveToCache({
        repoStructure,
        allFiles,
        loadedPaths: new Set([...loadedPaths, path])
      });

    } catch (error) {
      console.error(error);
    }
  };

  const navigateToPath = async (pathStr) => {
    const newPath = pathStr ? pathStr.split('/') : [];
    setCurrentPath(newPath);

    // Load directory contents if not already loaded
    if (!loadedPaths.has(pathStr)) {
      setIsLoading(true);
      await loadDirectoryContents(pathStr);
      setIsLoading(false);
    }

    // Hide search results when navigating
    setShowSearchResults(false);
    setSearchQuery('');
  };

  const getCurrentNode = useCallback(() => {
    let node = repoStructure;
    for (let part of currentPath) {
      if (node && node.children && node.children[part]) {
        node = node.children[part];
      } else {
        return null;
      }
    }
    return node;
  }, [repoStructure, currentPath]);

  const getCurrentItems = useCallback(() => {
    const currentNode = getCurrentNode();
    const items = [];

    if (currentNode && currentNode.children) {
      // Add folders
      for (let [key, child] of Object.entries(currentNode.children)) {
        if (child.type === 'folder') {
          items.push({
            name: child.name,
            type: 'folder',
            path: [...currentPath, key].join('/')
          });
        }
      }
    }

    // Add files in current directory
    const currentPathStr = currentPath.join('/');
    const filesInDir = filteredFiles.filter(file =>
      file.path === (currentPathStr ? currentPathStr + '/' : '')
    );

    items.push(...filesInDir.map(file => ({
      ...file,
      type: 'file'
    })));

    return items;
  }, [getCurrentNode, currentPath, filteredFiles]);

  const handleSearch = () => {
    if (searchQuery.trim().length === 0) {
      setShowSearchResults(false);
      return;
    }

    const query = searchQuery.trim().toLowerCase();
    const results = allFiles.filter(file =>
      file.name.toLowerCase().includes(query) ||
      file.path.toLowerCase().includes(query)
    );

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const applyFilters = () => {
    let filtered = [...allFiles];

    if (typeFilter) {
      filtered = filtered.filter(file => file.type === typeFilter);
    }

    if (languageFilter) {
      filtered = filtered.filter(file => file.lang === languageFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'date':
          return new Date(b.date) - new Date(a.date);
        case 'type':
          return a.type.localeCompare(b.type);
        default:
          return 0;
      }
    });

    setFilteredFiles(filtered);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const countFolders = (node) => {
    if (!node || node.type !== 'folder') return 0;
    let count = 1;
    if (node.children) {
      for (let child of Object.values(node.children)) {
        count += countFolders(child);
      }
    }
    return count;
  };

  const renderBreadcrumb = () => {
    const parts = ['Home', ...currentPath];
    return (
      <div className="mb-8 pb-4 border-b border-slate-200 dark:border-slate-700">
        <div className="overflow-x-auto scrollbar-thin max-w-full">
          <div className="flex items-center gap-2 text-sm min-w-max">
            {parts.map((part, index) => {
              const isLast = index === parts.length - 1;
              const path = parts.slice(1, index + 1).join('/');
              return (
                <React.Fragment key={index}>
                  <span
                    className={`${!isLast ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100 font-semibold'} px-2 py-1 rounded transition-colors flex items-center gap-2 whitespace-nowrap`}
                    onClick={() => !isLast && navigateToPath(index === 0 ? '' : path)}
                  >
                    {index === 0 ? <><Home className="w-4 h-4" /> {part}</> : part}
                  </span>
                  {!isLast && <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryTree = () => {
    if (!repoStructure) {
      return <div className="text-slate-500 dark:text-slate-400">Loading...</div>;
    }

    return (
      <TreeItem
        node={repoStructure}
        path={[]}
        isActive={currentPath.length === 0}
        onNavigate={navigateToPath}
        level={0}
      />
    );
  };

  const renderItems = (items) => {
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center text-slate-500 dark:text-slate-400">
          <FolderOpen className="w-16 h-16 mb-4 text-slate-300 dark:text-slate-600" />
          <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100">Empty Directory</h3>
          <p>No files or folders found here</p>
        </div>
      );
    }

    return items.map((item, index) => (
      <FileItem
        key={index}
        item={item}
        isFolder={item.type === 'folder'}
        onNavigate={navigateToPath}
        searchQuery={showSearchResults ? searchQuery : ''}
        viewMode={currentView}
      />
    ));
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2 text-slate-900 dark:text-slate-100">Error</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      <Header
        config={APP_CONFIG}
        darkMode={darkMode}
        toggleTheme={toggleTheme}
        handleClearCache={handleClearCache}
        lastUpdated={lastUpdated}
        formatLastUpdated={formatLastUpdated}
      />

      {/* Main Content */}
      <Routes>
        <Route path="/commits" element={<CommitHistory />} />
        <Route path="/" element={
          <div className="max-w-7xl mx-auto px-8 py-8 flex gap-8">
            {/* Sidebar */}
            <aside className="w-80 flex-shrink-0">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 sticky top-32 max-h-[calc(100vh-10rem)] overflow-y-auto shadow-sm">
                {/* Search */}
                <div className="mb-8">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search solutions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-10 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Category Tree */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4 font-semibold flex items-center gap-2">
                    <Folder className="w-4 h-4" /> Categories
                  </h3>
                  <div className="scrollbar-thin">
                    {renderCategoryTree()}
                  </div>
                </div>
              </div>
            </aside>

            {/* Content Area */}
            <main className="flex-1">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm h-[calc(100vh-10rem)] flex flex-col">
                <div className="p-8 pb-4 flex-shrink-0">
                  {renderBreadcrumb()}

                  {/* Toolbar */}
                  <div className="flex justify-between items-center">
                    <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
                      <button
                        onClick={() => setCurrentView('grid')}
                        className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${currentView === 'grid'
                          ? 'bg-white dark:bg-slate-800 text-blue-600 shadow-sm'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                          }`}
                      >
                        <LayoutGrid className="w-4 h-4" /> Grid
                      </button>
                      <button
                        onClick={() => setCurrentView('list')}
                        className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${currentView === 'list'
                          ? 'bg-white dark:bg-slate-800 text-blue-600 shadow-sm'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                          }`}
                      >
                        <List className="w-4 h-4" /> List
                      </button>
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="name">Sort by Name</option>
                      <option value="date">Sort by Date</option>
                      <option value="type">Sort by Type</option>
                    </select>
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto scrollbar-thin px-8 pb-8">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                      <p className="text-slate-500 dark:text-slate-400">Loading...</p>
                    </div>
                  ) : showSearchResults ? (
                    <div>
                      <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                        <Search className="w-5 h-5 text-blue-500" />
                        Search Results for "{searchQuery}" ({searchResults.length} found)
                      </h3>
                      <div className={currentView === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'flex flex-col gap-3'}>
                        {renderItems(searchResults.map(file => ({ ...file, type: 'file' })))}
                      </div>
                    </div>
                  ) : (
                    <div className={currentView === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'flex flex-col gap-3'}>
                      {renderItems(getCurrentItems())}
                    </div>
                  )}
                </div>
              </div>
            </main>
          </div>
        } />
      </Routes>

      <Footer config={APP_CONFIG} />
    </div>
  );
}

export default App;
